/**
 * Community+ business category taxonomy — CANONICAL SOURCE OF TRUTH (web).
 *
 * ⚠️ This file MUST be kept BYTE-IDENTICAL (taxonomy data + helpers) with its mobile mirror:
 *      mobile/src/data/businessCategories.ts
 *    The two repos are separate (mobile can't import from web src), so the taxonomy is duplicated
 *    by hand. When you change this list, make the SAME change in the mobile mirror. Only the header
 *    comment differs between the two files; everything below is identical.
 *
 * DESIGN:
 *  • 2-level taxonomy: 12 founder-locked PARENT categories, each with CHILD categories.
 *  • The child `label` is the CANONICAL STORED STRING — it is what gets written to
 *    businesses.category (a free-text column) when a listing picks a category. The child `id`
 *    (kebab-case) is for React keys / routing only, never stored.
 *  • `icon` is a lucide icon NAME string (not a component), resolved to lucide-react (web) or
 *    lucide-react-native (mobile) at the UI layer — this keeps the data identical across repos.
 *  • Child labels are globally unique, so findParentOfChild(label) is unambiguous.
 */

export interface BusinessChildCategory {
  /** kebab-case slug — keys/routing only, never stored. */
  id: string;
  /** Display label AND the canonical string stored in businesses.category. */
  label: string;
}

export interface BusinessParentCategory {
  /** kebab-case slug. */
  id: string;
  /** Display label. */
  label: string;
  /** lucide icon NAME (resolved to a component per platform at the UI layer). */
  icon: string;
  children: BusinessChildCategory[];
}

export const BUSINESS_CATEGORIES: readonly BusinessParentCategory[] = [
  {
    id: 'shopping-retail',
    label: 'Shopping & Retail',
    icon: 'ShoppingBag',
    children: [
      { id: 'halal-grocery-supermarkets', label: 'Halal Grocery & Supermarkets' },
      { id: 'meat-butchers', label: 'Meat & Butchers' },
      { id: 'islamic-clothing', label: 'Islamic Clothing' },
      { id: 'modest-fashion', label: 'Modest Fashion' },
      { id: 'hijab-abaya', label: 'Hijab & Abaya' },
      { id: 'islamic-books-bookstores', label: 'Islamic Books & Bookstores' },
      { id: 'islamic-gifts', label: 'Islamic Gifts' },
      { id: 'perfumes-attar', label: 'Perfumes & Attar' },
      { id: 'prayer-essentials', label: 'Prayer Essentials' },
      { id: 'islamic-art-decor', label: 'Islamic Art & Décor' },
      { id: 'jewellery', label: 'Jewellery' },
      { id: 'general-retail', label: 'General Retail' },
    ],
  },
  {
    id: 'food-dining',
    label: 'Food & Dining',
    icon: 'UtensilsCrossed',
    children: [
      { id: 'halal-restaurants', label: 'Halal Restaurants' },
      { id: 'halal-cafes', label: 'Halal Cafes' },
      { id: 'biryani-indian-cuisine', label: 'Biryani & Indian Cuisine' },
      { id: 'bakeries-sweets', label: 'Bakeries & Sweets' },
      { id: 'catering', label: 'Catering' },
      { id: 'halal-meat-bbq', label: 'Halal Meat & BBQ' },
      { id: 'food-delivery', label: 'Food Delivery' },
      { id: 'cloud-kitchens', label: 'Cloud Kitchens' },
    ],
  },
  {
    id: 'islamic-services',
    label: 'Islamic Services',
    icon: 'BookOpen',
    children: [
      { id: 'hajj-umrah', label: 'Hajj & Umrah' },
      { id: 'islamic-schools-madrasas', label: 'Islamic Schools & Madrasas' },
      { id: 'quran-classes', label: 'Quran Classes' },
      { id: 'arabic-classes', label: 'Arabic Classes' },
      { id: 'islamic-counselling', label: 'Islamic Counselling' },
      { id: 'nikah-services', label: 'Nikah Services' },
      { id: 'islamic-event-services', label: 'Islamic Event Services' },
      { id: 'janaza-services', label: 'Funeral services' },
      { id: 'quran-printing-publishing', label: 'Quran Printing & Publishing' },
    ],
  },
  {
    id: 'marriage-family',
    label: 'Marriage & Family',
    icon: 'Heart',
    children: [
      { id: 'muslim-matrimonial-services', label: 'Muslim Matrimonial Services' },
      { id: 'wedding-planners', label: 'Wedding Planners' },
      { id: 'nikah-venues', label: 'Nikah Venues' },
      { id: 'wedding-photography-videography', label: 'Wedding Photography & Videography' },
      { id: 'bridal-wear', label: 'Bridal Wear' },
      { id: 'groom-wear', label: 'Groom Wear' },
      { id: 'mehendi-makeup', label: 'Mehendi & Makeup' },
      { id: 'wedding-catering', label: 'Wedding Catering' },
    ],
  },
  {
    id: 'health-wellness',
    label: 'Health & Wellness',
    icon: 'Stethoscope',
    children: [
      { id: 'doctors', label: 'Doctors' },
      { id: 'dentists', label: 'Dentists' },
      { id: 'hospitals-clinics', label: 'Hospitals & Clinics' },
      { id: 'pharmacies', label: 'Pharmacies' },
      { id: 'physiotherapy', label: 'Physiotherapy' },
      { id: 'diagnostic-centres', label: 'Diagnostic Centres' },
      { id: 'mental-wellness', label: 'Mental Wellness' },
      { id: 'hijama', label: 'Hijama' },
      { id: 'unani-ayurvedic', label: 'Unani & Ayurvedic' },
      { id: 'fitness-gyms', label: 'Fitness & Gyms' },
    ],
  },
  {
    id: 'travel-transport',
    label: 'Travel & Transport',
    icon: 'Plane',
    children: [
      { id: 'muslim-friendly-travel', label: 'Muslim-Friendly Travel' },
      { id: 'travel-agencies', label: 'Travel Agencies' },
      { id: 'hotels', label: 'Hotels' },
      { id: 'car-rentals', label: 'Car Rentals' },
      { id: 'taxi-transport', label: 'Taxi & Transport' },
      { id: 'visa-immigration-services', label: 'Visa & Immigration Services' },
    ],
  },
  {
    id: 'home-property',
    label: 'Home & Property',
    icon: 'Home',
    children: [
      { id: 'real-estate', label: 'Real Estate' },
      { id: 'property-management', label: 'Property Management' },
      { id: 'construction', label: 'Construction' },
      { id: 'interior-design', label: 'Interior Design' },
      { id: 'architects', label: 'Architects' },
      { id: 'home-services', label: 'Home Services' },
      { id: 'electrical-plumbing', label: 'Electrical & Plumbing' },
      { id: 'furniture', label: 'Furniture' },
      { id: 'home-decor', label: 'Home Décor' },
      { id: 'pest-control', label: 'Pest Control' },
    ],
  },
  {
    id: 'professional-services',
    label: 'Professional Services',
    icon: 'Briefcase',
    children: [
      { id: 'chartered-accountants', label: 'Chartered Accountants' },
      { id: 'lawyers', label: 'Lawyers' },
      { id: 'tax-gst-consultants', label: 'Tax & GST Consultants' },
      { id: 'insurance', label: 'Insurance' },
      { id: 'financial-services', label: 'Financial Services' },
      { id: 'business-consultants', label: 'Business Consultants' },
      { id: 'digital-marketing', label: 'Digital Marketing' },
      { id: 'it-services', label: 'IT Services' },
      { id: 'graphic-designers', label: 'Graphic Designers' },
      { id: 'recruitment', label: 'Recruitment' },
      { id: 'printing', label: 'Printing' },
      { id: 'photography-videography', label: 'Photography & Videography' },
      { id: 'legal-documentation-notary', label: 'Legal Documentation & Notary' },
    ],
  },
  {
    id: 'education',
    label: 'Education',
    icon: 'GraduationCap',
    children: [
      { id: 'schools', label: 'Schools' },
      { id: 'coaching-centres', label: 'Coaching Centres' },
      { id: 'tuition', label: 'Tuition' },
      { id: 'universities-colleges', label: 'Universities & Colleges' },
      { id: 'vocational-training', label: 'Vocational Training' },
      { id: 'online-courses', label: 'Online Courses' },
      { id: 'quran-arabic-learning', label: 'Quran & Arabic Learning' },
      { id: 'educational-consultants', label: 'Educational Consultants' },
    ],
  },
  {
    id: 'automotive',
    label: 'Automotive',
    icon: 'Car',
    children: [
      { id: 'car-dealers', label: 'Car Dealers' },
      { id: 'used-cars', label: 'Used Cars' },
      { id: 'auto-repair', label: 'Auto Repair' },
      { id: 'car-wash', label: 'Car Wash' },
      { id: 'spare-parts', label: 'Spare Parts' },
      { id: 'tyres-batteries', label: 'Tyres & Batteries' },
      { id: 'driving-schools', label: 'Driving Schools' },
    ],
  },
  {
    id: 'community-lifestyle',
    label: 'Community & Lifestyle',
    icon: 'Users',
    children: [
      { id: 'ngos-charities', label: 'NGOs & Charities' },
      { id: 'community-organisations', label: 'Community Organisations' },
      { id: 'youth-organisations', label: 'Youth Organisations' },
      { id: 'sports-clubs', label: 'Sports Clubs' },
      { id: 'kids-activities', label: 'Kids Activities' },
      { id: 'event-venues', label: 'Event Venues' },
      { id: 'community-centres', label: 'Community Centres' },
      { id: 'senior-care', label: 'Senior Care' },
      { id: 'social-community-services', label: 'Social & Community Services' },
    ],
  },
  {
    id: 'other-services',
    label: 'Other Services',
    icon: 'MoreHorizontal',
    children: [
      { id: 'other-businesses', label: 'Other Businesses' },
      { id: 'other-professional-services', label: 'Other Professional Services' },
    ],
  },
];

/** Flat array of every child label (the canonical stored strings), in taxonomy order. */
export function getAllChildLabels(): string[] {
  return BUSINESS_CATEGORIES.flatMap((parent) => parent.children.map((child) => child.label));
}

/** The parent whose children include this label, or undefined if the label isn't in the taxonomy. */
export function findParentOfChild(label: string): BusinessParentCategory | undefined {
  return BUSINESS_CATEGORIES.find((parent) => parent.children.some((child) => child.label === label));
}

/** O(1) membership set of every valid child label. */
export const CHILD_LABELS: ReadonlySet<string> = new Set(getAllChildLabels());
